import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import NumericInput from 'react-numeric-input';
import InputWrapper from '../../common/InputWrapper';
import { getCurrencySymbol } from '../../../helpers';
import { VAT_RATES } from '../../../config';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  .react-numeric-input {
    flex: 1 1;
    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .prices {
    display: grid;
    grid-template-columns: 1fr;
  }

  .single-on-row {
    grid-column: auto;
  }

  @media (min-width: 576px) {
    .single-on-row {
      grid-column: 1 / 3;
    }

    .prices {
      grid-template-columns: 1fr 1fr;
      grid-gap: 20px;
    }
  }
`;

const PriceBlock = (props) => {
  const { currentProduct, values, showNdsField, handleChangeInput } = props;

  const { priceSettings } = useSelector((state) => state.settings);
  const [showAnotherPrices, setShowAnotherPrices] = useState(false);

  const { t } = useAutoTranslation();

  const handleChangePrice = (value, priceType) => {
    const isExist = Boolean(values.prices[priceType.code]);
    const newPrice = isExist
      ? {
          ...values.prices[priceType.code],
          value,
        }
      : {
          priceCode: priceType.code,
          priceName: priceType.name,
          value,
        };

    handleChangeInput('prices', {
      ...values.prices,
      [priceType.code]: newPrice,
    });
  };

  const basicPrice = priceSettings.find((p) => p.name === 'Розничная цена');
  const beforeDiscountPrice = priceSettings.find((p) => p.name === 'Цена до скидки');
  const anotherPrices = priceSettings.filter(
    (p) =>
      p.name !== 'Розничная цена' &&
      p.name !== 'Цена до скидки' &&
      p.name !== 'Средняя цена закупки',
  );

  const getFormattedAnotherPrices = (anotherPrices) => {
    const anotherPricesBeforeSale = anotherPrices.filter((p) => p.code.includes('до скидки'));
    const anotherPricesAfterSale = anotherPrices.filter((p) => !p.code.includes('до скидки'));

    const compareFirstLetters = (a, b) => {
      const aLetter = a.name?.toLowerCase()?.[0];
      const bLetter = b.name?.toLowerCase()?.[0];
      return aLetter > bLetter ? 1 : aLetter < bLetter ? -1 : 0;
    };

    const couplesPrices = anotherPricesBeforeSale.sort(compareFirstLetters).reduce((acc, p) => {
      const pricesAfterSale = anotherPricesAfterSale.find((curPricesAfterSale) =>
        p.code.includes(curPricesAfterSale.code),
      );
      if (pricesAfterSale) {
        acc.push(pricesAfterSale);
        acc.push(p);
        const pricesAfterSaleIndex = anotherPricesAfterSale.findIndex((curPricesAfterSale) =>
          p.code.includes(curPricesAfterSale.code),
        );
        anotherPricesAfterSale.splice(pricesAfterSaleIndex, 1);
      } else {
        acc.push(p);
      }
      return acc;
    }, []);

    return [...couplesPrices, ...anotherPricesAfterSale.sort(compareFirstLetters)];
  };

  const formattedAnotherPrices = getFormattedAnotherPrices(anotherPrices);

  const isElementHaveDiscountForCurrentCompany = (idx) => {
    const currentPrice = formattedAnotherPrices[idx];
    const nextPrice = formattedAnotherPrices[idx + 1];

    let isCurrentPriceHaveDiscount, isNextPriceHaveDiscount;

    if (currentPrice)
      isCurrentPriceHaveDiscount = currentPrice.name.includes('до скидки') ? true : false;
    if (nextPrice) isNextPriceHaveDiscount = nextPrice.name.includes('до скидки') ? true : false;

    if (isCurrentPriceHaveDiscount) {
      return true;
    } else if (isNextPriceHaveDiscount) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Wrapper className="card-box" style={{ height: 'auto' }}>
      <div className="row">
        <div className="col-12">
          <h4 className="header-title">{t('Цены')}</h4>
        </div>
      </div>
      {basicPrice ? (
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Цена')}
                id="price"
                value={
                  values.prices[t('Розничная цена')] ? values.prices[t('Розничная цена')].value : ''
                }
                onChange={(valueAsNumber) => handleChangePrice(valueAsNumber, basicPrice)}
                textAppend={getCurrencySymbol(basicPrice?.currency)}
                placeholder={t('Введите цену')}
                component={NumericInput}
                componentProps={{ precision: 2, style: false }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {beforeDiscountPrice ? (
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Цена до скидки')}
                id="oldPrice"
                value={
                  values.prices[t('Цена до скидки')] ? values.prices[t('Цена до скидки')].value : ''
                }
                onChange={(valueAsNumber) => handleChangePrice(valueAsNumber, beforeDiscountPrice)}
                textAppend={getCurrencySymbol(beforeDiscountPrice?.currency)}
                placeholder={t('Введите цену до скидки')}
                component={NumericInput}
                componentProps={{ precision: 2, style: false }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {anotherPrices.length && !showAnotherPrices ? (
        <div
          style={{ color: 'rgb(126, 87, 194)', cursor: 'pointer' }}
          onClick={() => setShowAnotherPrices(true)}
        >
          {t('Все цены')}
        </div>
      ) : null}

      {formattedAnotherPrices.length && showAnotherPrices ? (
        <div className="prices">
          {formattedAnotherPrices.map((p, idx) => (
            <div
              className={`form-group ${
                isElementHaveDiscountForCurrentCompany(idx) ? '' : 'single-on-row'
              }`}
              key={p.code}
            >
              <InputWrapper
                type="text"
                label={t(p.name)}
                id={p.code}
                value={values.prices[p.code] ? values.prices[p.code].value : ''}
                onChange={(valueAsNumber) => handleChangePrice(valueAsNumber, p)}
                textAppend={getCurrencySymbol(p?.currency)}
                placeholder={t('Введите цену')}
                component={NumericInput}
                componentProps={{ precision: 2, style: false }}
              />
            </div>
          ))}
        </div>
      ) : null}

      {showNdsField ? (
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Ставка НДС')}
                id="vat"
                value={VAT_RATES.find((r) => r.value === currentProduct.vat).label}
                disabled
              />
            </div>
          </div>
        </div>
      ) : null}
    </Wrapper>
  );
};

export default PriceBlock;
